import _ from 'lodash';

import {logger} from './log.helper';

import {
  IS_MOBILE,
  IS_TABLET
} from './device-info.utils';

/**
 * Small utilities that don't fit anywhere else. These utilities MUST NOT depend on
 * any AngularJS injections.
 */

/**
 * Variadic function; creates a new Error with given arguments JSON.stringify'd.
 *
 * @returns {Error}
 */

function _createError(...args) {
  return new Error(JSON.stringify(args));
}

/**
 * Variadic function; logs error with logger
 */
function logError(...args) {
  logger().error('Encountered error', args, (new Error()).stack);
}

/**
 * Variadic function; throws given arguments as JSON.stringify'd error.
 *
 * @throws
 */
function throwError(...args) {
  logError(...args);
  throw _createError(...args);
}

/**
 * Variadic function; returns given arguments as JSON.stringify'd error.
 *
 * @returns {Error}
 */
function getError(...args) {
  logError(...args)
  return _createError(...args)
}

/**
 * Trigger an event on a DOM element.
 * @param {HTMLElement} element
 * @param {string} event Name of event to trigger.
 * @param {boolean} canBubble
 * @param {boolean} cancelable
 */
function triggerEvent(element, event, canBubble=false, cancelable=true) {
  if ('createEvent' in document) {
    let e = document.createEvent('HTMLEvents');
    e.initEvent(event, canBubble, cancelable);
    element.dispatchEvent(e);
  }
  else {
    // Version for IE10 and older
    element.fireEvent(event);
  }
}

/**
 * A function that does nothing.
 *
 * Wondering why this exists? Sometimes we want to explicitly state that some callback or
 * condition does nothing. For example promise rejections that are not handled will log an
 * error into the console. So we can hide that and state explicitly that we are not handling
 * the rejection and take all responsibility with:
 *
 *     returnsPromise().catch(noop);
 *
 * Remember, with great noop comes great responsibility.
 */
function noop() {}

/**
 * Binary match over an array of values to find the highest matching value, using the given matcher.
 *
 * Suitable for use with numeric, sortable data.
 *
 * @param {Array} array Array to search, MUST be sorted! Matching values should be lower, unmatching
 *   should be higher.
 * @param {Function} matcher Matcher function to use, gets the value to be matched as an
 *   argument. Must return false if value does not match and true if it does.
 * @returns {*} The matched value or false if no match was found.
 */
function binaryMatchHighest(array, matcher) {
  const getNextSplit = array => {
    const index = Math.ceil(array.length / 2) - 1;
    return [array[index], index];
  };

  let len = array.length;
  let lastMatchedVal = null;

  while (len > 0) {
    const [val, splitIndex] = getNextSplit(array);
    const match = matcher(val);

    if (len === 1) {
      if (match) {
        return val;
      }
      else if (lastMatchedVal !== null) {
        return lastMatchedVal;
      }
      else {
        return false;
      }
    }

    if (match) {
      lastMatchedVal = val;
      array = array.slice(splitIndex + 1);
    }
    else {
      array = array.slice(0, splitIndex + 1);
    }

    len = array.length;
  }

  return false;
}

const NAME_RE = /function\s([^(]+)\(/;

/**
 * Return the class name of a given instance.
 *
 * IE has no support for constructor.name, which is why we need to use this method.
 *
 * @param {object} instance
 * @returns {string} Name of class or empty string if cannot be retrieved.
 */
function className(instance) {
  const results = NAME_RE.exec(instance.constructor.toString());
  return (results.length > 1)? results[1].trim() : '';
}

/**
 * Flip object so that keys become values and vice versa. Object must have unique values or some values may be lost!
 * @param {object} object
 * @param {boolean} symbol_keys If given, handle the input object's keys as symbols (with getOwnPropertySymbols).
 * @return {object}
 */
function flipObject(object, symbol_keys=false) {
  const keys = symbol_keys? Object.getOwnPropertySymbols(object) : Object.keys(object);

  return keys.reduce((acc, key) => {
    acc[object[key]] = key;
    return acc;
  }, {});
}

/**
 * Escape a string for safe inclusion into a RegExp.
 * @param {string} str
 * @return {string}
 */
function escapeForRegExp(str) {
  // eslint-disable-next-line no-useless-escape
  return str.replace(/[-[\]{}()*+!<=:?.\/\\^$|#\s,]/g, '\\$&');
}

/**
 * Copy object with symbol keys.
 *
 * Copying objects with symbol keys does not work with normal angular.copy or _.clone. Additionally, looping with
 * Object.keys does not work either, as it returns an empty list. We need to use Object.getOwnPropertySymbols and it's
 * clumsy, so here's a util function just for that.
 *
 * @param {Object.<Symbol, *>} object Object with symbol keys.
 * @param {boolean} deep Set to true to take a deep copy instead of a shallow copy. Then _.cloneDeep() is called to copy
 *   the values. Default is to do a shallow copy.
 */
function copySymbolObject(object, deep=false) {
  return Object.getOwnPropertySymbols(object).reduce((acc, symbol) => {
    acc[symbol] = deep? _.cloneDeep(object[symbol]) : object[symbol];
    return acc;
  }, {});
}

/**
 * Outputs count amount of non breaking spaces to be used as native padding.
 *
 * @param {Number} count Space count
 * @return {string} String with set amount of spaces.
 * @private
 */
function printSpaces(count=2) {
  let result = "";
  for (let i = 0; i < count; i++) {
    result += String.fromCharCode(160);
  }
  return result;
}

/**
 * Function to determine if given element is visible in the client viewport
 * @param {DOMElement|angular.element|JQLite} element
 * @return {boolean} The result
 */
function elementIsInsideViewport(element = null) {
  if (element) {
    const domRect = element instanceof angular.element ? element[0].getBoundingClientRect() : element.getBoundingClientRect();
    return domRect &&
      domRect.top >= 0 &&
      domRect.left >= 0 &&
      domRect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
      domRect.right <= (window.innerWidth || document.documentElement.clientWidth);
  }
  return false;
}

/**
 * Function to determine if given element is above client viewport
 * @param {DOMElement|angular.element|JQLite} element
 * @return {boolean} The result
 */
function elementIsAboveViewport(element = null) {
  if (element) {
    const domRect = element instanceof angular.element ? element[0].getBoundingClientRect() : element.getBoundingClientRect();
    return domRect && domRect.top < 0;
  }
  return false;
}


/**
 * Hack to change native MDCSelect selection wihtout user action
 * @param {Number} step Move selected index this much forward or backward
 */
function forceMoveNativeMdcSelectSelection(step) {
  if (IS_MOBILE || IS_TABLET) {
    let selectElem, select, originalIndex;
    // DOM must be ready...
    angular.element(() => {
      try {
        selectElem = document.getElementsByClassName('mdc-select-native')[0];
        select = new window.mdc.MDCSelect.attachTo(selectElem);

        if (select) {
          originalIndex = select.selectedIndex;
          select.selectedIndex += step
          select.emit('MDCSelect:change');
        }
      } catch (err) {
        logger().error(`Couldn't move MDCSelect to ${originalIndex + step} by ${step} from ${originalIndex}: ${err}`, selectElem);

        if (select && originalIndex) {
          select.selectedIndex = originalIndex;
          select.emit('MDCSelect:change');
        }
      }
    });
  }
}


export {
  logError,
  throwError,
  getError,
  triggerEvent,
  noop,
  binaryMatchHighest,
  className,
  flipObject,
  escapeForRegExp,
  copySymbolObject,
  printSpaces,
  elementIsInsideViewport,
  elementIsAboveViewport,
  forceMoveNativeMdcSelectSelection,
};



// WEBPACK FOOTER //
// ./src/app/common/common.utils.js